import { flow, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { oktaAuth, OKTA_REDIRECT_URL } from '../oktaAuth'
import { loginLink } from '../router'
import { isOversightControl } from './rootStore'

export const LoginStore = FetchModel.named('UserLoginStore')
  .props({
    loginStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    oversightLoginStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    logOutStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get gcsOktaLoginUrl(): string {
      return `${self.baseUrl}users/okta_callback/`
    },
    get oversightOktaLoginUrl(): string {
      return `${self.oversightBaseUrl}login/okta_callback`
    },
    get logoutGcsUrl(): string {
      return `${self.baseUrl}users/logout/`
    },
    get logoutOversightUrl(): string {  
      return `${self.oversightBaseUrl}user/logout`
    }
  }))
  .actions((_self) => ({
    _logoutFromOkta: flow(function* _logoutFromOkta() {
      const isLoggedInOkta = yield* toGenerator(oktaAuth.session.exists())
      if (isLoggedInOkta) {
        yield* toGenerator(oktaAuth.signOut())
      }
    }),
  }))
  .actions((self) => ({
    loginToGcsWithAuthorizationCode: flow(function* loginToGcsWithAuthorizationCode(authorization_code: string) {
      self.loginStatus = OperationInProgress()

      const loginInfo = {
        code: authorization_code,
        redirect_uri: OKTA_REDIRECT_URL,
      }

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.gcsOktaLoginUrl,
          { method: 'POST', body: JSON.stringify(loginInfo) },
          { fetchOperationName: 'Login user with authorization code' },
        ),
      )

      if (fetchResult.error) {
        self.loginStatus = OperationFailure(fetchResult.error)
      } else {
        self.loginStatus = OperationSuccess()
      }
    }),
    loginToOversightWithAuthorizationCode: flow(function* loginToOversightWithAuthorizationCode(authorization_code: string) {
      self.oversightLoginStatus = OperationInProgress()

      const loginInfo = {
        code: authorization_code,
        redirect_uri: OKTA_REDIRECT_URL,
      }

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.oversightOktaLoginUrl,
          { method: 'POST', body: JSON.stringify(loginInfo) },
          { fetchOperationName: 'Login to oversight with authorization code' },
        ),
      )

      if (fetchResult.error) {
        self.oversightLoginStatus = OperationFailure(fetchResult.error)
      } else {
        self.oversightLoginStatus = OperationSuccess()
      }
    }),
    logOut: flow(function* logout() {
      self.logOutStatus = OperationInProgress()

      if (!isOversightControl()) {
        const logOutGcsResult = yield* toGenerator(
          self.loadObjectOrError<void>(self.logoutGcsUrl, { method: 'POST' }, { fetchOperationName: 'Log out user from GCS' }),
        )

        if (logOutGcsResult.error) {
          console.error('Error logging out of GCS backend', logOutGcsResult.error)
          self.logOutStatus = OperationFailure(logOutGcsResult.error)
          return
        }
      }

      const logOutOversightResult = yield* toGenerator(
        self.loadObjectOrError<void>(self.logoutOversightUrl, { method: 'POST' }, { fetchOperationName: 'Log out user from Oversight' }),
      )

      if (logOutOversightResult.error) {
        console.error('Error logging out of Oversight backend', logOutOversightResult.error)
        self.logOutStatus = OperationFailure(logOutOversightResult.error)
        return
      }

      self.logOutStatus = OperationSuccess()
      void self._logoutFromOkta() // logout from okta, just in case we couldn't log out after login.
      loginLink.push()
    }),
  }))
